"use client";

import type React from "react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { SyncDirection } from "../sync-direction-icon";
import {
  candidateSyncDescription,
  candidateSyncLabel,
  syncDirectionCopy,
} from "../../lib/sync-direction-copy";

/**
 * Per-candidate ATS sync-state, keyed on `PipelineCandidateResponse.ats_sync_state`.
 * Supersedes the old `ats_application_linked` boolean as the row/drawer indicator.
 *
 * Rendered as a soft gradient-tinted pill (the `linear` Badge variant) so it
 * reads as part of the same visual family as the Stage badge, with a per-state
 * `--gradient-color`: gray = idle, blue = in-flight, green = synced, red = failed.
 * Each state carries a hover tooltip describing what it means; the `error` state
 * surfaces the actual failure message when one is available.
 */
const LABELS: Record<
  string,
  { label: string; color: string; description: string }
> = {
  not_synced: {
    label: "Not synced",
    color: "#6b7280",
    description: "This candidate hasn't been synced to the ATS yet.",
  },
  queued: {
    label: "Syncing",
    color: "#4c9aff",
    description: "Pushing this candidate to the ATS.",
  },
  synced: {
    label: "Synced",
    color: "#34d399",
    description: "This candidate is synced with the ATS.",
  },
  error: {
    label: "Sync failed",
    color: "#f87171",
    description: "Sync failed. Try syncing this candidate again.",
  },
  ineligible: {
    label: "Ineligible",
    color: "#f59e0b",
    description:
      "This candidate can't be synced yet — some required information is missing.",
  },
};

const PAUSED_COLOR = "#6b7280";
const PAUSED_DESCRIPTION =
  "Sync is paused — the job's ATS link is dormant, so this candidate's last synced state is frozen.";
const UPDATING_DESCRIPTION = "Updating this candidate in the ATS.";

/**
 * In-flight queued label: create (or null for migration-safe legacy creates)
 * → Syncing; any other pending action → Updating.
 */
export function queuedSyncLabel(
  pendingAction?: string | null,
  direction?: SyncDirection | null,
): string {
  if (pendingAction == null || pendingAction === "create_application") {
    return syncDirectionCopy(direction).inFlight;
  }
  return "Updating";
}

/**
 * The tooltip copy for a given sync state, or null when there's nothing to show
 * (unknown / missing state). For `error`, the real failure message wins when
 * present, falling back to the generic description. Exported so the description
 * can be unit-tested without driving a hover interaction.
 */
export function syncStateDescription(
  state: string | undefined,
  error?: string | null,
  paused?: boolean,
  pendingAction?: string | null,
  direction?: SyncDirection | null,
): string | null {
  if (!state || !(state in LABELS)) return null;
  if (!!paused && state === "synced") return PAUSED_DESCRIPTION;
  if (state === "error")
    return error?.trim()
      ? error
      : candidateSyncDescription(state, direction, pendingAction);
  if (state === "ineligible")
    return error?.trim() ? error : LABELS.ineligible.description;
  if (
    state === "queued" &&
    queuedSyncLabel(pendingAction, direction) === "Updating"
  ) {
    return UPDATING_DESCRIPTION;
  }
  return candidateSyncDescription(state, direction, pendingAction);
}

export function SyncStateBadge({
  state,
  error,
  paused,
  pendingAction,
  direction,
  className,
}: {
  state?: string;
  error?: string | null;
  direction?: SyncDirection | null;
  /**
   * In-flight push action from `ats_sync_pending_action`. Only affects the
   * `queued` label/tooltip (Syncing vs Updating).
   */
  pendingAction?: string | null;
  /** Extra classes on the Badge — lets a host match sibling chips (e.g. the
   * profile header's 26px stage badge) without changing the table-row default. */
  className?: string;
  /**
   * The job's ATS link is dormant (unlinked-but-preserved) — everything
   * freezes locally. Only changes the rendering of a `synced` candidate,
   * whose sync state is otherwise frozen at its last known value: shown as
   * "Paused" (muted gray, rather than the usual green synced tint) so it
   * reads as inert rather than actively in sync.
   */
  paused?: boolean;
}) {
  if (!state || !(state in LABELS)) return null;
  const cfg = LABELS[state];
  const isPausedSynced = !!paused && state === "synced";
  const label = isPausedSynced
    ? "Paused"
    : candidateSyncLabel(state, direction, pendingAction);
  const color = isPausedSynced ? PAUSED_COLOR : cfg.color;
  const description = syncStateDescription(
    state,
    error,
    paused,
    pendingAction,
    direction,
  );
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="linear"
          size="xs"
          className={className}
          style={{ "--gradient-color": color } as React.CSSProperties}
        >
          {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{description}</TooltipContent>
    </Tooltip>
  );
}

/**
 * A candidate can be (re-)synced only once it has left the system Sourced stage
 * and is either not yet synced or in a failed state. Queued/synced candidates
 * are excluded — they're already in flight or done.
 */
export function isSyncActionEnabled(
  state: string | undefined,
  isInSystemSourced: boolean,
  direction?: SyncDirection | null,
): boolean {
  if (direction === "pull_only") return false;
  return !isInSystemSourced && (state === "not_synced" || state === "error");
}

/**
 * Human-readable reason the Sync action is unavailable, or null when it's
 * enabled. Drives the disabled-button `title` on the drawer/row surfaces.
 */
export function syncDisabledReason(
  state: string | undefined,
  isInSystemSourced: boolean,
  direction?: SyncDirection | null,
): string | null {
  if (isSyncActionEnabled(state, isInSystemSourced, direction)) return null;
  if (direction === "pull_only") return "Can't push to this ATS";
  if (state === "synced") return "Already synced";
  if (state === "queued") return "Sync in progress";
  if (state === "ineligible") return "Missing required info";
  if (isInSystemSourced) return "Move out of Sourced to sync";
  return null;
}
